import { useDebouncedValue } from "@gettactic/map-builder/src/components/map/hooks/useDebouncedValue";
import type { TextShape } from "@gettactic/map-builder/src/types";
import React from "react";
import { useEditorSelector, useEditorStore } from "../store/EditorStoreContext";
import { buildWorldShapeTransform } from "./shapeTransform";

/**
 * Inline editing of a text shape (double-click in the select tool, or the
 * text tool on existing text). A textarea sits exactly over the shape in a
 * foreignObject; edits commit debounced without history entries, and the
 * whole edit becomes one entry when the editor closes.
 */
export const TextEditorOverlay: React.FC = () => {
  const store = useEditorStore();
  const targetId = useEditorSelector((state) =>
    state.chrome.highlightedShape?.shape.type === "text"
      ? (state.chrome.highlightedShape.targetId ??
        state.chrome.highlightedShape.shape.id)
      : null
  );
  const shape = useEditorSelector((state) =>
    targetId ? state.shapes[targetId] : undefined
  );
  if (shape?.type !== "text") return null;
  return <TextEditor key={shape.id} shape={shape} store={store} />;
};

interface TextEditorProps {
  shape: TextShape;
  store: ReturnType<typeof useEditorStore>;
}

const TextEditor: React.FC<TextEditorProps> = ({ shape, store }) => {
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const { value, onChange, flush } = useDebouncedValue(shape.text, (text) =>
    store.getState().updateShape(shape.id, { text })
  );

  React.useEffect(() => {
    const state = store.getState();
    const before = {
      shapes: state.shapes,
      order: state.order,
      selection: state.selection
    };
    state.setHistoryRecording(false);
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.focus();
      // Caret at the end: typing continues the text instead of replacing it.
      const end = textarea.value.length;
      textarea.setSelectionRange(end, end);
    }
    return () => {
      // One history entry for the whole edit, recorded on close.
      const after = store.getState();
      after.setHistoryRecording(true);
      if (after.shapes !== before.shapes) {
        after.commit((document) => document, { history: false });
        store.setState({
          history: {
            past: [...after.history.past, before],
            future: []
          }
        });
      }
    };
  }, [store]);

  const close = () => {
    flush();
    store.getState().setChrome({ highlightedShape: null });
  };

  const fontSize = shape.fontSize || 20;
  const height = Math.max(shape.height, fontSize * 1.5);
  return (
    <foreignObject
      x={shape.x}
      y={shape.y}
      width={Math.max(shape.width, 20)}
      height={height + 10}
      transform={buildWorldShapeTransform(shape)}
      pointerEvents="all"
    >
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={close}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            close();
          }
          event.stopPropagation();
        }}
        onPointerDown={(event) => event.stopPropagation()}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          padding: 0,
          margin: 0,
          background: "rgba(255,255,255,0.6)",
          outline: "1px dashed #0D99FF",
          resize: "none",
          overflow: "hidden",
          fontSize: `${fontSize}px`,
          lineHeight: 1,
          fontFamily: `"${shape.fontFamily || "Quicksand"}"`,
          fontWeight: shape.isBold ? 700 : 400,
          fontStyle: shape.isItalic ? "italic" : "normal",
          textAlign: shape.textAlign || "left",
          color: shape.textColor || "#B5B5B5",
          whiteSpace: "normal",
          overflowWrap: "break-word"
        }}
      />
    </foreignObject>
  );
};
